// quyoshli-preview — w26 "Quyoshli" card-preview.
//
// A warm golden-hour wedding taklifnoma. Outer frame is a peach-to-
// amber sunrise gradient, inside an ivory card with an amber hairline,
// a sun medallion in place of a photo (rays + central disc behind a
// circular photo mat), and italic Cormorant names. Same outer-frame +
// inner-card structure as rose-gold so all cards read together.
import { Photo, type LayoutCtx } from "./_shared";

export function QuyoshliPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const FOIL = `linear-gradient(135deg, #FFD8A6 0%, ${accent} 30%, #F2C18A 55%, ${accent} 80%, #C97A33 100%)`;
  const [a, b] = names.split(/\s*&\s*/);
  const coral = "#E8754A";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: coral,
            marginBottom: "5%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* Sun medallion — rays + central circle + photo mat. */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          {/* Rays — radiating outwards from the disc. */}
          <svg
            viewBox="-100 -100 200 200"
            style={{ position: "absolute", inset: "-12%", width: "124%", height: "124%", pointerEvents: "none" }}
            aria-hidden
          >
            <g stroke={accent} strokeWidth="1.4" strokeLinecap="round" fill="none" opacity="0.55">
              {Array.from({ length: 16 }).map((_, i) => {
                const ang = (i * Math.PI * 2) / 16;
                const x1 = Math.sin(ang) * 60;
                const y1 = -Math.cos(ang) * 60;
                const x2 = Math.sin(ang) * 94;
                const y2 = -Math.cos(ang) * 94;
                return (
                  <line
                    key={i}
                    x1={x1.toFixed(1)}
                    y1={y1.toFixed(1)}
                    x2={x2.toFixed(1)}
                    y2={y2.toFixed(1)}
                  />
                );
              })}
            </g>
          </svg>

          {/* Foil disc ring */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOIL,
              padding: "5%",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "4%",
                boxSizing: "border-box",
                boxShadow: `inset 0 0 0 1px ${accent}55`,
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at 50% 45%, #FFE9C2, ${accent}55 60%, ${bg} 90%)`,
                      }}
                    />
                  }
                />
                {/* Soft warm wash from below. */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(180deg, transparent 50%, ${accent}22 100%)`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Names — italic Cormorant, stacked across "&". */}
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 30,
            fontWeight: 500,
            lineHeight: 1.0,
            color: ink,
          }}
        >
          {a ?? names}
        </div>
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 22,
            color: coral,
            margin: "2px 0",
            lineHeight: 0.85,
          }}
        >
          &amp;
        </div>
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 30,
            fontWeight: 500,
            lineHeight: 1.0,
            color: ink,
          }}
        >
          {b ?? ""}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: coral,
            marginTop: "4%",
          }}
        >
          TO'Y MAROSIMI · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
